import { Dataset } from '../../../types/types';
import { ReportTemplate, ReportTemplateSummary } from '../types';
import {
  buildDefaultReportTemplate,
  getDefaultTemplateId,
} from './buildDefaultReportTemplate';
import { isReportTemplateLayout } from './validateReportTemplate';

export type ResolvedReportTemplate = {
  template: ReportTemplate;
  /** True when the requested id was not found and the default was used. */
  isFallback: boolean;
};

/**
 * Picks the template to print.
 *
 * The generated system default is always available, for every dataset. Stored
 * templates (F2, report_templates) are looked up first once they exist; a
 * missing id falls back to the default rather than failing the print, and says
 * so through isFallback.
 */
export const resolveReportTemplate = (
  dataset: Dataset,
  templateId: string | undefined,
  storedTemplates: ReportTemplate[] = [],
  storedFailed = false
): ResolvedReportTemplate => {
  // A stored layout is used as loaded; the caller fetched it by this very id, so
  // there is nothing to match on (a stored template carries whatever templateId
  // the designer saved, not the row id it lives under).
  const datasetId = String(dataset.datasetId ?? '');
  const stored = storedTemplates.find(
    (candidate) =>
      isReportTemplateLayout(candidate) && candidate.datasetId === datasetId
  );

  if (stored && !storedFailed) {
    return { template: stored, isFallback: false };
  }

  const template = buildDefaultReportTemplate(dataset);
  const wantedDefault =
    !templateId || templateId === getDefaultTemplateId(template.datasetId);

  return {
    template,
    isFallback:
      storedFailed || (storedTemplates.length > 0 && !stored) || !wantedDefault,
  };
};

/**
 * Templates offered in the print menu: the generated default plus whatever is
 * stored for the dataset.
 */
export const listReportTemplates = (
  dataset: Dataset,
  storedTemplates: { id: string; datasetId: string; name: string }[] = []
): ReportTemplateSummary[] => {
  const datasetId = String(dataset.datasetId ?? '');
  const storedForDataset = storedTemplates.filter(
    (template) => template.datasetId === datasetId
  );
  const normalizedNames = storedForDataset.map((template) =>
    template.name.trim().toLocaleLowerCase()
  );
  const nameCounts = normalizedNames.reduce<Map<string, number>>(
    (counts, name) => counts.set(name, (counts.get(name) ?? 0) + 1),
    new Map()
  );
  const seenNames = new Map<string, number>();

  return [
    {
      templateId: getDefaultTemplateId(datasetId),
      label: 'print.defaultTemplate',
      isSystemDefault: true,
    },
    ...storedForDataset.map((template, index) => {
      const normalizedName = normalizedNames[index];
      const duplicateIndex = (seenNames.get(normalizedName) ?? 0) + 1;
      seenNames.set(normalizedName, duplicateIndex);
      return {
        templateId: template.id,
        label:
          (nameCounts.get(normalizedName) ?? 0) > 1
            ? `${template.name} (${duplicateIndex})`
            : template.name,
      };
    }),
  ];
};
